/**
 * WarehouseKpiPanel.jsx — iter206
 *
 * Read-only operational KPI panel that layers on top of the existing
 * pick-route / warehouse optimisation module. Renders inside the
 * WarehouseLayoutDesigner as a slide-down section under the overlay toolbar.
 *
 * KPIs surfaced:
 *   1. Labour hours     (headline tile)
 *   2. Cost per order   (headline tile)
 *   3. Picks per hour   (headline tile)
 *   4. Lines per hour   (headline tile)
 *   5. Cost per client  (table — sortable, top 50)
 *   6. Cost per SKU     (table — sortable, top 50)
 *   7. Shift performance (compact 3-column card row)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ChartLine, Clock, CurrencyDollar, Package, X, ArrowClockwise } from '@phosphor-icons/react';
import { getWarehouseLayoutKpis } from '../lib/api';

const money = (n) => `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n, dp = 2) => (Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: dp });

const HeadlineTile = ({ label, value, sub, icon: Icon, tone = 'brand' }) => (
  <div
    data-testid={`kpi-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
    className="border border-border bg-white dark:bg-slate-900 px-4 py-3 flex flex-col gap-1"
  >
    <div className="flex items-center gap-2">
      {Icon && <Icon size={14} className={tone === 'brand' ? 'text-brand' : 'text-muted-foreground'} weight="fill" />}
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
    </div>
    <div className="text-2xl font-bold tabular-nums leading-tight">{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground tabular-nums">{sub}</div>}
  </div>
);

const ShiftCard = ({ row, rate }) => (
  <div className="border border-border p-3 flex flex-col gap-2 bg-muted/10" data-testid={`kpi-shift-${row.shift}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-bold uppercase tracking-widest">{row.shift}</span>
      <Badge variant="outline" className="text-[9px]">{row.orders} orders</Badge>
    </div>
    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
      <span className="text-muted-foreground">Hours</span>
      <span className="tabular-nums text-right">{num(row.labour_hours)}</span>
      <span className="text-muted-foreground">Cost</span>
      <span className="tabular-nums text-right">{money(row.cost)}</span>
      <span className="text-muted-foreground">Picks/hr</span>
      <span className="tabular-nums text-right">{num(row.picks_per_hour)}</span>
      <span className="text-muted-foreground">Lines/hr</span>
      <span className="tabular-nums text-right">{num(row.lines_per_hour)}</span>
      <span className="text-muted-foreground">$/order</span>
      <span className="tabular-nums text-right">{money(row.cost_per_order)}</span>
    </div>
  </div>
);

const Table = ({ title, rows, columns, testid, emptyText }) => (
  <div className="border border-border bg-white dark:bg-slate-900" data-testid={testid}>
    <div className="px-3 py-2 border-b border-border flex items-center justify-between">
      <span className="text-xs font-bold uppercase tracking-widest">{title}</span>
      <span className="text-[10px] text-muted-foreground">{rows.length} row{rows.length === 1 ? '' : 's'}</span>
    </div>
    {rows.length === 0 ? (
      <p className="p-3 text-xs text-muted-foreground">{emptyText}</p>
    ) : (
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 sticky top-0">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={`text-left px-3 py-1.5 font-semibold ${c.numeric ? 'text-right' : ''}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-border/60">
                {columns.map((c) => (
                  <td key={c.key} className={`px-3 py-1.5 tabular-nums ${c.numeric ? 'text-right' : ''}`}>
                    {c.render ? c.render(r) : (r[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export const WarehouseKpiPanel = ({ warehouseId, onClose }) => {
  const [days, setDays] = useState(30);
  const [labourRate, setLabourRate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!warehouseId) return;
    setLoading(true);
    try {
      const r = await getWarehouseLayoutKpis(warehouseId, days, labourRate ? Number(labourRate) : undefined);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, [warehouseId, days, labourRate]);

  useEffect(() => { load(); }, [load]);

  if (!warehouseId) return null;
  const h = data?.headline || {};

  return (
    <Card className="p-4 space-y-4 border-2 border-brand/40" data-testid="warehouse-kpi-panel">
      {/* Header row */}
      <div className="flex items-center flex-wrap gap-3 pb-2 border-b border-border">
        <div className="flex items-center gap-2">
          <ChartLine size={18} className="text-brand" weight="fill" />
          <h3 className="text-sm font-bold uppercase tracking-widest">Warehouse KPIs</h3>
        </div>
        <Badge variant="outline" className="text-[10px]">
          Last {data?.window_days ?? days} days · Labour {money(data?.labour_rate ?? 32)}/hr
        </Badge>

        <div className="flex items-center gap-1 ml-auto">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Days</Label>
          <Input
            type="number" min={1} max={365} value={days}
            onChange={(e) => setDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)))}
            className="w-20 h-8 text-xs" data-testid="kpi-days-input"
          />
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground ml-2">$/hr</Label>
          <Input
            type="number" min={0} max={1000} step="0.5" value={labourRate}
            placeholder="32.00"
            onChange={(e) => setLabourRate(e.target.value)}
            className="w-20 h-8 text-xs" data-testid="kpi-rate-input"
          />
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="kpi-refresh-btn">
            <ArrowClockwise size={12} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="kpi-close-btn">
            <X size={14} />
          </Button>
        </div>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-headline">
        <HeadlineTile
          label="Labour Hours" value={num(h.labour_hours)}
          sub={`${h.orders_shipped ?? 0} orders shipped`}
          icon={Clock}
        />
        <HeadlineTile
          label="Cost / Order" value={money(h.cost_per_order)}
          sub={`Total spend ${money(h.cost)}`}
          icon={CurrencyDollar}
        />
        <HeadlineTile
          label="Picks / Hour" value={num(h.picks_per_hour)}
          sub={`${num(h.total_picks, 0)} picks total`}
          icon={Package}
        />
        <HeadlineTile
          label="Lines / Hour" value={num(h.lines_per_hour)}
          sub={`${num(h.total_lines, 0)} lines touched`}
          icon={Package}
        />
      </div>

      {/* Shift performance */}
      <div>
        <p className="overline mb-2">Shift performance</p>
        {(data?.by_shift?.length ?? 0) === 0 ? (
          <p className="text-xs text-muted-foreground border border-dashed border-border p-3">
            No completed pick/pack tasks with timestamps in this window.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="kpi-shifts">
            {(data.by_shift || []).map((row) => (
              <ShiftCard key={row.shift} row={row} rate={data.labour_rate} />
            ))}
          </div>
        )}
      </div>

      {/* Cost per client + Cost per SKU tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Table
          title="Cost per client"
          testid="kpi-client-table"
          rows={data?.by_client || []}
          emptyText="No 3PL merchant activity — cost per client shows here when orders have a merchant_id attached."
          columns={[
            { key: 'name', label: 'Client' },
            { key: 'orders', label: 'Orders', numeric: true, render: (r) => num(r.orders, 0) },
            { key: 'labour_hours', label: 'Hours', numeric: true, render: (r) => num(r.labour_hours) },
            { key: 'cost', label: 'Cost', numeric: true, render: (r) => money(r.cost) },
            { key: 'cost_per_order', label: '$/order', numeric: true, render: (r) => money(r.cost_per_order) },
          ]}
        />
        <Table
          title="Cost per SKU"
          testid="kpi-sku-table"
          rows={data?.by_sku || []}
          emptyText="No SKU pick activity in this window."
          columns={[
            { key: 'sku_code', label: 'SKU', render: (r) => r.sku_code || r.sku_id?.slice(-6) },
            { key: 'name', label: 'Name', render: (r) => (r.name || '').slice(0, 32) },
            { key: 'lines', label: 'Lines', numeric: true, render: (r) => num(r.lines, 0) },
            { key: 'cost', label: 'Cost', numeric: true, render: (r) => money(r.cost) },
            { key: 'cost_per_line', label: '$/line', numeric: true, render: (r) => money(r.cost_per_line) },
          ]}
        />
      </div>

      {/* Per-person performance */}
      <Table
        title="Performance by person"
        testid="kpi-person-table"
        rows={data?.by_person || []}
        emptyText="No picker or packer activity in this window."
        columns={[
          { key: 'name', label: 'Person' },
          { key: 'labour_hours', label: 'Hours', numeric: true, render: (r) => num(r.labour_hours) },
          { key: 'picks', label: 'Picks', numeric: true, render: (r) => num(r.picks, 0) },
          { key: 'lines', label: 'Lines', numeric: true, render: (r) => num(r.lines, 0) },
          { key: 'picks_per_hour', label: 'Picks/hr', numeric: true, render: (r) => num(r.picks_per_hour) },
          { key: 'cost', label: 'Cost', numeric: true, render: (r) => money(r.cost) },
        ]}
      />

      <p className="text-[10px] text-muted-foreground italic">
        Labour hours are derived from `started_at → completed_at` on completed pick lists and pack tasks.
        Cost = hours × labour rate. Set a tenant-wide rate via Settings → Labour hourly rate, or override here.
      </p>
    </Card>
  );
};

export default WarehouseKpiPanel;
